import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportDowRevenueHeatmap } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtRate, fmtSignedRate } from "../shared";

const fmtIdx = (value) => (value == null ? "—" : Number(value).toFixed(2));

const gapTone = (value) => {
  if (value == null) return "text-gray-500 dark:text-gray-400";
  if (value > 0) return "text-blue-600 dark:text-blue-400";
  if (value < 0) return "text-red-600 dark:text-red-400";
  return "text-gray-500 dark:text-gray-400";
};

// Indigo ramp matches the CI heatmap palette guidance (see CLAUDE.md + DowHeatmapPanel).
const cellShade = (value, max) => {
  if (value == null || !max) return { background: "rgba(156, 163, 175, 0.04)" };
  const ratio = Math.max(0, Math.min(1, value / max));
  const alpha = 0.08 + ratio * 0.55;
  return { background: `rgba(99, 102, 241, ${alpha.toFixed(3)})` };
};

const DowRevenueHeatmapBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No DOW revenue data for the selected category." />;
  }

  // Single shared scale across both series so "You" vs "Market" are visually comparable.
  const allValues = rows.flatMap((row) => [row.your_revenue_index, row.market_revenue_index]).filter((value) => value != null);
  const maxIdx = allValues.length ? Math.max(...allValues) : 0;

  const youPeak = Math.max(...rows.map((row) => row.your_revenue_index ?? -Infinity));
  const mktPeak = Math.max(...rows.map((row) => row.market_revenue_index ?? -Infinity));

  return (
    <div className="p-5 space-y-5">
      <ThinScroll className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
              <th className="px-3 py-2.5 text-left font-semibold sticky left-0 bg-gray-50 dark:bg-gray-800 z-20">Series</th>
              {rows.map((row) => (
                <th key={`h-${row.dow_num}`} className="px-3 py-2.5 text-center font-semibold">{row.day_name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b dark:border-gray-800 bg-sky-50 dark:bg-gray-800">
              <td className="px-3 py-2.5 whitespace-nowrap sticky left-0 z-10 font-semibold text-[#32B1FF] bg-sky-50 dark:bg-gray-800">YOU</td>
              {rows.map((row) => {
                const value = row.your_revenue_index;
                const isPeak = value != null && value === youPeak && youPeak > 0;
                return (
                  <td key={`y-${row.dow_num}`} className="px-1 py-1 text-center" style={cellShade(value, maxIdx)}>
                    <div className={`tabular-nums text-sm py-1.5 ${isPeak ? "font-bold text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-200"}`}>
                      {fmtIdx(value)}
                    </div>
                  </td>
                );
              })}
            </tr>
            <tr className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <td className="px-3 py-2.5 whitespace-nowrap sticky left-0 z-10 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 font-medium">Market</td>
              {rows.map((row) => {
                const value = row.market_revenue_index;
                const isPeak = value != null && value === mktPeak && mktPeak > 0;
                return (
                  <td key={`m-${row.dow_num}`} className="px-1 py-1 text-center" style={cellShade(value, maxIdx)}>
                    <div className={`tabular-nums text-sm py-1.5 ${isPeak ? "font-bold text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-200"}`}>
                      {fmtIdx(value)}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
        <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
          Cell shade scales to the highest revenue index across both series. Bold = peak day for that series.
        </div>
      </ThinScroll>

      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Day</th>
              <th className="px-3 py-2.5 text-right font-semibold">Sample</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Occupancy</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your RevIdx</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Occupancy</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market RevIdx</th>
              <th className="px-3 py-2.5 text-right font-semibold">Rate Gap</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.dow_num} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Day" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{row.day_name}</td>
                <td data-label="Sample" className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{row.date_windows ?? 0}</td>
                <td data-label="Your Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 font-semibold whitespace-nowrap">{fmtRate(row.your_avg_rate)}</td>
                <td data-label="Your Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.your_avg_occupancy} /></td>
                <td data-label="Your RevIdx" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 font-semibold whitespace-nowrap">{fmtIdx(row.your_revenue_index)}</td>
                <td data-label="Market Rate" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg_rate)}</td>
                <td data-label="Market Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.market_avg_occupancy} /></td>
                <td data-label="Market RevIdx" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtIdx(row.market_revenue_index)}</td>
                <td data-label="Rate Gap" className={`px-3 py-2.5 text-right font-semibold whitespace-nowrap ${gapTone(row.rate_gap)}`}>
                  {fmtSignedRate(row.rate_gap)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
          Revenue index = rate × occupancy / 100. Higher = more captured revenue per available night.
        </div>
      </ThinScroll>
    </div>
  );
};

const DowRevenueHeatmapPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`dow-revenue-heatmap-${clientId}-${categoryCode || "all"}`}
    title="DoW Revenue Heatmap"
    tip="Your revenue index (rate × occupancy) by day of week, vs the market's. A weak day for you that's strong for the market = a rate-sensitivity test opportunity."
    description="Your revenue index vs market by day of week — combines rate and occupancy into a single capture metric."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportDowRevenueHeatmap(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? [...data].sort((rowA, rowB) => (rowA.dow_num ?? 0) - (rowB.dow_num ?? 0)) : [];
      return (
        <>
          <PanelTakeaway panelKey="dow_revenue_heatmap" say={say} />
          <DowRevenueHeatmapBody rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default DowRevenueHeatmapPanel;
